/**
 * ReportFiltersBar — per-report filter controls, Run and Approve buttons.
 * Stage 3 UI extraction from ReportsPage.js
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Save, Settings2 } from 'lucide-react';

const ReportFiltersBar = ({
  activeReport,
  filters, setFilters,
  sales, accounts,
  saleFilterData, loadingFilterData,
  loading, canRunReport, onRun,
  data, saving, onSave,
  onCustomizeFields,
}) => {
  if (!activeReport || activeReport === 'custom') return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-end gap-3 flex-wrap">
          {/* Daily Sale Updates: three required cascading dropdowns */}
          {activeReport === 'daily-sale-updates' && (
            <>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Sale <span className="text-red-500">*</span></span>
                <Select
                  value={filters.sale_id}
                  onValueChange={v => setFilters(f => ({ ...f, sale_id: v, crm_entity_id: '', opportunity_id: '' }))}
                >
                  <SelectTrigger className="w-48 h-9 text-sm" data-testid="filter-sale-select">
                    <SelectValue placeholder="Select Sale..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sales.map(s => {
                      const year = s.sale_start_date ? new Date(s.sale_start_date).getFullYear() : '';
                      const company = s.sale_company && s.sale_company.toLowerCase() !== 'other' ? s.sale_company : (s.sale_other_company || '');
                      const label = [year, company, s.sale_name].filter(Boolean).join(' ');
                      return <SelectItem key={s.id} value={s.id}>{label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Account <span className="text-red-500">*</span></span>
                <Select
                  value={filters.crm_entity_id}
                  onValueChange={v => setFilters(f => ({ ...f, crm_entity_id: v, opportunity_id: '' }))}
                  disabled={!filters.sale_id || loadingFilterData}
                >
                  <SelectTrigger className="w-48 h-9 text-sm" data-testid="filter-account-select">
                    <SelectValue placeholder={loadingFilterData ? 'Loading...' : 'Select Account...'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all_accounts__">— All Accounts —</SelectItem>
                    {saleFilterData.accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {filters.crm_entity_id !== '__all_accounts__' && (
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Hip # / Horse <span className="text-red-500">*</span></span>
                <Select
                  value={filters.opportunity_id}
                  onValueChange={v => setFilters(f => ({ ...f, opportunity_id: v }))}
                  disabled={!filters.crm_entity_id}
                >
                  <SelectTrigger className="w-56 h-9 text-sm" data-testid="filter-hip-select">
                    <SelectValue placeholder="Select Horse..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const ownerOpps = saleFilterData.opportunities
                        .filter(o => o.account_ids.includes(filters.crm_entity_id));
                      return (
                        <>
                          {ownerOpps.length > 1 && (
                            <SelectItem value="__all__">— All Horses ({ownerOpps.length}) —</SelectItem>
                          )}
                          {ownerOpps.map(o => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.hip_number ? `#${o.hip_number} — ` : ''}{o.horse_name || 'Unknown'}
                            </SelectItem>
                          ))}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
              )}
              {/* Session filter — All Accounts mode only, shown when the sale has sessions */}
              {filters.crm_entity_id === '__all_accounts__' && (saleFilterData.sessions || []).length > 0 && (
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Session</span>
                <Select
                  value={filters.session_id || '__all_sessions__'}
                  onValueChange={v => setFilters(f => ({ ...f, session_id: v === '__all_sessions__' ? '' : v }))}
                  disabled={!filters.sale_id}
                  data-testid="filter-session-select"
                >
                  <SelectTrigger className="w-44 h-9 text-sm">
                    <SelectValue placeholder="All Sessions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all_sessions__">— All Sessions —</SelectItem>
                    {(saleFilterData.sessions || []).map(s => {
                      const dateLabel = s.date
                        ? new Date(`${String(s.date).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : '';
                      return (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{dateLabel ? ` · ${dateLabel}` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              )}
            </>
          )}

          {/* Net proceeds: independent sale + account dropdowns */}
          {activeReport === 'net-proceeds' && (
            <>
              <Select value={filters.sale_id} onValueChange={v => setFilters(f => ({ ...f, sale_id: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-48 h-9 text-sm"><SelectValue placeholder="All Sales" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sales</SelectItem>
                  {sales.map(s => {
                    const year = s.sale_start_date ? new Date(s.sale_start_date).getFullYear() : '';
                    const company = s.sale_company && s.sale_company.toLowerCase() !== 'other' ? s.sale_company : (s.sale_other_company || '');
                    const label = [year, company, s.sale_name].filter(Boolean).join(' ');
                    return <SelectItem key={s.id} value={s.id}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Select value={filters.crm_entity_id} onValueChange={v => setFilters(f => ({ ...f, crm_entity_id: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-48 h-9 text-sm"><SelectValue placeholder="All Accounts" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.system_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}

          {/* Sales performance: group-by selector */}
          {activeReport === 'sales-performance' && (
            <Select value={filters.group_by} onValueChange={v => setFilters(f => ({ ...f, group_by: v }))}>
              <SelectTrigger className="w-48 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sire">By Sire</SelectItem>
                <SelectItem value="dam">By Dam</SelectItem>
                <SelectItem value="year">By Crop Year</SelectItem>
                <SelectItem value="sale">By Sale</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Date range — shown for all reports except DSU */}
          {activeReport !== 'daily-sale-updates' && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground whitespace-nowrap">From</span>
              <Input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} className="w-36 h-9 text-sm" style={filters.start_date ? { color: '#000' } : {}} />
              <span className="text-xs text-muted-foreground whitespace-nowrap">To</span>
              <Input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} className="w-36 h-9 text-sm" style={filters.end_date ? { color: '#000' } : {}} />
            </div>
          )}

          <Button variant="outline" size="sm" className="h-9" onClick={onCustomizeFields}>
            <Settings2 className="w-4 h-4 mr-1" /> Customize Fields
          </Button>
          <Button
            className="bg-primary hover:bg-primary/90 text-white h-9"
            onClick={onRun}
            disabled={loading || !canRunReport(activeReport)}
            title={activeReport === 'daily-sale-updates' && !canRunReport(activeReport)
              ? (filters.crm_entity_id === '__all_accounts__'
                  ? 'Please select a Sale to run this report'
                  : 'Please select Sale, Account, and Hip # to run this report')
              : ''}
          >
            {loading ? 'Running...' : 'Run Report'}
          </Button>
          {activeReport === 'daily-sale-updates' && !canRunReport(activeReport) && (
            <span className="text-xs text-muted-foreground">
              {filters.crm_entity_id === '__all_accounts__'
                ? 'Select a Sale to run'
                : 'Select all three filters to run'}
            </span>
          )}
          {data && (
            <div className="flex gap-2 ml-auto">
              <Button size="sm" className="h-9 bg-primary hover:bg-primary/90 text-white" onClick={onSave} disabled={saving}>
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-1" />Approve Report</>}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportFiltersBar;
